import type { CardType, CreatureKind } from '../types/catalog';
import { CREATURE_KINDS } from '../types/catalog';
import { CARD_TYPE_LABEL_PLURAL, CREATURE_KIND_LABEL } from '../i18n/labels';

/** Estado dos filtros da página de cartas. */
export interface Filters {
  search: string;
  types: Set<CardType>;
  kinds: Set<CreatureKind>;
}

export const EMPTY_FILTERS: Filters = {
  search: '',
  types: new Set<CardType>(),
  kinds: new Set<CreatureKind>(),
};

const ALL_TYPES: CardType[] = ['unit', 'spell', 'trap'];

interface FilterBarProps {
  filters: Filters;
  onChange: (next: Filters) => void;
  typeCounts: Record<CardType, number>;
  availableKinds: Set<CreatureKind>;
}

/** Alterna a presença de um valor num Set imutavelmente. */
function toggle<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export function FilterBar({
  filters,
  onChange,
  typeCounts,
  availableKinds,
}: FilterBarProps): React.JSX.Element {
  const hasActiveFilters =
    filters.search !== '' || filters.types.size > 0 || filters.kinds.size > 0;

  // Tags de criatura só são relevantes quando "unidades" está incluído no filtro.
  const showKinds = filters.types.size === 0 || filters.types.has('unit');

  return (
    <div className="filterbar">
      <div className="filter-row">
        <input
          type="search"
          className="search-input"
          placeholder="Buscar por nome ou descrição…"
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          aria-label="Buscar cartas"
        />
        {hasActiveFilters && (
          <button type="button" className="btn-clear" onClick={() => onChange(EMPTY_FILTERS)}>
            Limpar filtros
          </button>
        )}
      </div>

      <div className="filter-row">
        <span className="filter-label">Tipo</span>
        {ALL_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            className={`chip type-${type} ${filters.types.has(type) ? 'active' : ''}`}
            aria-pressed={filters.types.has(type)}
            onClick={() => {
              const types = toggle(filters.types, type);
              // Filtros de facção só valem para unidades. Se o tipo selecionado
              // deixar de incluir unidades, limpamos as facções para não criar
              // um filtro "invisível" que zera os resultados.
              const keepKinds = types.size === 0 || types.has('unit');
              onChange({
                ...filters,
                types,
                kinds: keepKinds ? filters.kinds : new Set<CreatureKind>(),
              });
            }}
          >
            {CARD_TYPE_LABEL_PLURAL[type]}
            <span className="chip-count">{typeCounts[type]}</span>
          </button>
        ))}
      </div>

      {showKinds && availableKinds.size > 0 && (
        <div className="filter-row">
          <span className="filter-label">Grupo</span>
          {CREATURE_KINDS.filter((k) => availableKinds.has(k)).map((kind) => (
            <button
              key={kind}
              type="button"
              className={`chip ${filters.kinds.has(kind) ? 'active' : ''}`}
              aria-pressed={filters.kinds.has(kind)}
              onClick={() => onChange({ ...filters, kinds: toggle(filters.kinds, kind) })}
            >
              {CREATURE_KIND_LABEL[kind]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
